import React, { useEffect, useState } from "react";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Save, AlertCircle } from "lucide-react";

const FIELDS = [
  { k: "company_name",    l: "Company name",          w: "Shown in templates as {{company_name}}" },
  { k: "owner_name",      l: "Owner / signer name",   w: "Goes in signature blocks" },
  { k: "company_address", l: "Street address",        w: "Replaces [Street Address] in your templates" },
  { k: "company_city",    l: "City",                  w: "" },
  { k: "company_state",   l: "State (2-letter)",      w: "" },
  { k: "company_zip",     l: "ZIP",                   w: "Replaces [ZIP] in your templates" },
  { k: "company_phone",   l: "Phone",                 w: "" },
  { k: "company_email",   l: "Email",                 w: "Used as inquiry@ contact" },
];

export default function Settings() {
  const { get, patch } = useApi();
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    get("/settings/company").then(r => setData(r.data));
  }, []); // eslint-disable-line

  const onChange = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await patch("/settings/company", data);
      setData(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const incomplete = !data.company_address || !data.company_zip;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Settings</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Company Profile
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          These values fill the letterhead on every master template — `[Street Address]` and `[ZIP]`
          get replaced automatically when a claim package is generated. Set them once.
        </p>
      </div>

      {incomplete && (
        <Card className="p-3 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-900">
              Your letterhead is incomplete. Templates will print &quot;[Street Address]&quot; or
              &quot;[ZIP]&quot; literally until you save real values here.
            </p>
          </div>
        </Card>
      )}

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4 text-slate-500" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">ClaimWerks letterhead</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(f => (
            <div key={f.k}>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{f.l}</Label>
              <Input
                value={data[f.k] || ""}
                onChange={(e) => onChange(f.k, e.target.value)}
                data-testid={`settings-${f.k}-input`}
                className="mt-1"
              />
              {f.w && <p className="text-[10px] text-slate-500 mt-1">{f.w}</p>}
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="settings-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200 bg-slate-50">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
          Default contingency
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Default fee % (used when contact has none)</Label>
            <Input
              type="number" min="0" max="50" step="1"
              value={data.default_contingency_fee_pct ?? 30}
              onChange={(e) => onChange("default_contingency_fee_pct", Number(e.target.value))}
              data-testid="settings-default-fee-input"
              className="mt-1"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              State caps still override (TX 25%, GA 10%, NJ 20%).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
